import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { getMyOffers, isAdmin, Offer } from '../../api';
import { compact, PageHead, StatusBadge, usePortal } from './shared';

const rate = (part: number, whole: number) => (whole > 0 ? `${Math.round((part / whole) * 100)}%` : '–');

// Per-offer funnel from data the platform actually records: views of the offer page, claims, and
// claims redeemed at the venue. Admin only: reached from the admin console's act-as view; a venue's
// own partner login is sent back to the dashboard.
const AnalyticsPage: React.FC = () => {
  const { to } = usePortal();
  if (!isAdmin()) return <Navigate to={to('/partner-account')} replace />;
  return <AnalyticsReport />;
};

const AnalyticsReport: React.FC = () => {
  const [offers, setOffers] = useState<Offer[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    getMyOffers().then(setOffers).catch(() => setFailed(true));
  }, []);

  const rows = [...(offers ?? [])].sort((a, b) => b.claimsCount - a.claimsCount || b.views - a.views);
  const total = rows.reduce((t, o) => ({ views: t.views + o.views, claims: t.claims + o.claimsCount, redeemed: t.redeemed + o.redeemedCount }), { views: 0, claims: 0, redeemed: 0 });

  return (
    <>
      <PageHead title="Analytics" subtitle="How customers respond to your offers." />
      {failed && <div className="pp-notice error">Could not load analytics. Please refresh.</div>}
      <div className="pp-stats">
        <div className="pp-stat"><b>{compact(total.views)}</b><p>Offer Views</p></div>
        <div className="pp-stat"><b>{compact(total.claims)}</b><p>Claims</p></div>
        <div className="pp-stat"><b>{rate(total.claims, total.views)}</b><p>Views → Claims</p></div>
        <div className="pp-stat"><b>{rate(total.redeemed, total.claims)}</b><p>Claims Redeemed</p></div>
      </div>
      <h3 className="pp-section">By Offer</h3>
      <div className="pp-panel">
        {offers === null && !failed ? (
          <p className="pp-muted" style={{ margin: 0 }}>Loading...</p>
        ) : rows.length === 0 ? (
          <p className="pp-muted" style={{ margin: 0 }}>No offers yet. Publish one to start seeing views and claims.</p>
        ) : (
          <div className="pp-table-wrap">
            <table className="pp-table">
              <thead>
                <tr><th>Offer</th><th>Status</th><th className="num">Views</th><th className="num">Claims</th><th className="num">Redeemed</th><th className="num">Views → Claims</th></tr>
              </thead>
              <tbody>
                {rows.map((o) => (
                  <tr key={o.id}>
                    <td><b>{o.title}</b>{o.benefit && <><br /><span className="pp-muted">{o.benefit}</span></>}</td>
                    <td><StatusBadge status={o.status} /></td>
                    <td className="num">{o.views}</td>
                    <td className="num">{o.claimsCount}{o.maxClaims ? <span className="pp-muted"> / {o.maxClaims}</span> : null}</td>
                    <td className="num">{o.redeemedCount}</td>
                    <td className="num">{rate(o.claimsCount, o.views)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="pp-hint">Views count each time a customer opens an offer's page. Claims made on your venue's older single offer aren't included here.</p>
      </div>
    </>
  );
};

export default AnalyticsPage;
